/**
 * Générateur — Automatisme : déterminer a % de c quand a vaut 100, 50, 25,
 * 10, 1.
 * Attendu ID : 1128 | Niveau : 4ème | Domaine : Proportionnalité
 * Rubrique : AUTOMATISME (programme 2026, Proportionnalité 4e) — rituel court.
 * Labels (cartes exerciseur) :
 *   - Calculer a % de c              (saisie ; QCM à leurres calculés ; V/F sur des calculs)
 *   - Le bon geste                   (relier pourcentage ↔ geste, avec cibles leurres ; QCM à l'envers : 12 est quel pourcentage de 48 ?)
 *   - Les gestes : vrai ou faux ?    (V/F de pur rappel)
 *
 * ── Plan de cartes validé (2026-09-08) ─────────────────────────────────────
 * Source : BO cycle 4 (2026), Proportionnalité 4e, automatisme « Déterminer
 * a % de c quand a vaut 100, 50, 25, 10, 1 » + grille P1-P12. P1 : CM2
 * introduit (fraction d'une quantité), 6e formalisé (définition du
 * pourcentage, fraction opérateur sur un nombre), 5e automatisme (1 %, 10 %,
 * 50 % d'un nombre), 4e étendu à 100 % et 25 %. P2 : sens installés — a % =
 * a sur 100, 50 % moitié, 25 % quart, 10 % dixième, 1 % centième, 100 % le
 * tout ; non installés (distracteurs) — coefficient multiplicateur,
 * augmentation/diminution, a % quelconque, pourcentage > 100. P3 : c entier
 * ≤ 500, résultats entiers ou à une décimale (jamais deux). P4 : fraction
 * d'une quantité. P6 : aucune calculatrice. P7 : pas de produit en croix ni
 * de coefficient : chaque pourcentage est un geste. P8 : pourcentage, moitié,
 * quart, dixième, centième. P9 : ÷ 2, ÷ 4, ÷ 10, ÷ 100 restent implicites.
 * P10 : constat. P11 : 25 % lu « ÷ 25 », 10 % lu « − 10 », 1 % confondu avec
 * 0,1, 100 % qui ne change rien, résultat donné en %. P12 : rémanence forte.
 * INVARIANTS :
 *   - a ∈ {100, 50, 25, 10, 1} ; c choisi pour que a % de c ait au plus une
 *     décimale ; tous les calculs en ENTIERS de dixièmes ;
 *   - leurres calculés, distincts, positifs ; V/F : pas de crochets, mixité
 *     1-3 faux, familles exclusives, fenêtre stricte.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie, makeRelier,
    retirerCodesErreur,
} from '../../../utils';

const ATTENDU_ID = 1128;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CALCUL = 'Calculer a % de c';
const L_GESTE = 'Le bon geste';
const L_RAPPEL = 'Les gestes : vrai ou faux ?';

const A = [100, 50, 25, 10, 1];
// Revue 2026-09-08 (A8) : 100 % de c se recopie ; ce cas reste un rappel à 10 %, les quatre gestes se partagent le reste.
const tirerA = () => (Math.random() < 0.1 ? 100 : randomChoice([50, 25, 10, 1]));
const fr = (x) => String(Math.round(x * 10) / 10).replace('.', ',');
/** a % de c en dixièmes entiers (null si le résultat a plus d'une décimale). */
const dixiemes = (a, c) => { const d = (a * c) / 10; return Number.isInteger(d) ? d : null; };
const valeur = (a, c) => dixiemes(a, c) / 10;
const GESTE = { 100: 'le nombre entier', 50: 'la moitié', 25: 'le quart', 10: 'le dixième', 1: 'le centième' };

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ──────────────
// Gestes voisins de ceux du QCM (construireCalculQcm) ; le vrai/faux y ajoute le sien (voisinVf).
const VOISINS = { 100: [50], 50: [25, 100], 25: [50, 10], 10: [25, 1], 1: [10] };
const voisinVf = (a) => (a === 100 ? 50 : a === 1 ? 10 : a * 2);
/** Les gestes faux nommés de « a % de c » et leur résultat exact. */
const erreursCalcul = (a, c, voisins = VOISINS[a]) => [
    // Relecture du 04/10/2026 (E31) : pour a = 1, c ÷ 1 = c a trois histoires (÷ 1, × 1, le nombre recopié) ;
    // pour a = 100, c ÷ 100 est aussi 1 % de c (repère voisin). « ÷ a » ne se nomme que pour 25 et 50.
    [a === 1 || a === 100 ? null : 'divise_par_a', c / a],
    ['moins_a', c - a],
    ...voisins.map((b) => ['pourcentage_voisin', (b * c) / 100]),
    ['pourcent_lu_dixiemes', (a * c) / 10],
    ...(a === 50 ? [['pourcentage_voisin', c / 10]] : []),
];
/** Les erreurs sûres : valeurs positives, ni la bonne réponse, ni portées par deux codes. */
const erreursSures = (paires, juste) => {
    const parValeur = [];
    paires.forEach(([code, v]) => {
        if (!Number.isFinite(v) || v <= 0 || Math.abs(v - juste) < 1e-9) return;
        const deja = parValeur.find((p) => Math.abs(p.v - v) < 1e-9);
        if (deja) deja.codes.add(code); else parValeur.push({ v, codes: new Set([code]) });
    });
    // code null : une histoire sans nom, qui bloque aussi tout autre code de même valeur
    return parValeur.filter((p) => p.codes.size === 1 && !p.codes.has(null)).map((p) => ({ code: [...p.codes][0], v: p.v }));
};
/** Écriture exacte au centième près (virgule décimale), comme les réponses de l'élève. */
const fr2 = (x) => String(Math.round(x * 100) / 100).replace('.', ',');
const auDixiemePres = (x) => Math.abs(x * 10 - Math.round(x * 10)) < 1e-9;
// Codes des cibles leurres du relier, repris du catalogue commun.
const CODE_LEURRE = { divise_par_a: 'divise_par_a', moins_a: 'moins_a', multiplie: 'multiplie_par_a' };

/** c adapté à a : résultat entier ou à une décimale, c ≤ 500 ; la moitié des tirages en entiers. */
const tirerC = (a) => {
    for (let essai = 0; essai < 100; essai++) {
        let c;
        if (a === 100) c = randInt(12, 500);
        // Calcul mental (règle prof) : moitiés et quarts sous 200, quarts à résultat demi sous 100.
        else if (a === 50) c = randomChoice([randInt(6, 100) * 2, randInt(3, 99) * 2 + 1]);             // entier ou demi
        else if (a === 25) c = randomChoice([randInt(3, 50) * 4, randInt(2, 24) * 4 + 2]);               // entier ou demi (jamais 3,75)
        else if (a === 10) c = randomChoice([randInt(2, 50) * 10, randInt(12, 499)]);                     // entier ou un dixième
        else c = randomChoice([randInt(1, 5) * 100, randInt(2, 50) * 10]);                                // 1 % : entier ou un dixième
        if (c > 500 || dixiemes(a, c) === null) continue;
        return c;
    }
    return 200;
};

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refS = { v: null }; const refQ = { v: null }; const refG = { v: null }; const refI = { v: null };

// ─── Carte 1a : calculer a % de c (saisie) ─────────────────────────────────

const construireCalculSaisie = () => {
    const a = tirerA(); const c = tirerC(a);
    const payload = makeSaisie(fr(valeur(a, c)), `<p>Calcule de tête : ${a} % de ${c}.</p>`, opts(L_CALCUL));
    payload.contenu = { ...payload.contenu, sousType: `a${a}` };
    payload._meta.params = { a, c, signature: `${a}-${c}` };
    // Les gestes faux du QCM, plus la division arrêtée au quotient entier, écrits au centième près.
    const v = valeur(a, c);
    const paires = [...erreursCalcul(a, c), ...(Number.isInteger(v) ? [] : [['reste_oublie', Math.floor(v)]])];
    const erreurs = erreursSures(paires, v).filter((e) => Math.abs(e.v * 100 - Math.round(e.v * 100)) < 1e-9).map((e) => ({ code: e.code, valeur: fr2(e.v) }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genCalculSaisie = () => antiRepeat(construireCalculSaisie, refS);

// ─── Carte 1b : calculer a % de c (QCM à leurres calculés) ────────────────

const construireCalculQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const a = tirerA(); const c = tirerC(a); const v = valeur(a, c);
        const voisins = { 100: [50], 50: [25, 100], 25: [50, 10], 10: [25, 1], 1: [10] };
        const pool = [
            { v: c / a, role: 'divise_par_a' },                                        // 25 % lu « ÷ 25 »
            { v: c - a, role: 'moins_a' },                                             // 10 % lu « − 10 »
            { v: valeur(randomChoice(voisins[a]), c), role: 'pourcentage_voisin' },    // le geste d'à côté
            { v: a, role: 'a_lui_meme' },                                              // le pourcentage recopié
            { v: v * 10, role: 'dix_fois' },                                           // erreur de rang (÷ 10 au lieu de ÷ 100)
            { v: v / 10, role: 'dixieme' },
        ];
        // Revue 2026-09-08 (A3) : un leurre au-dessus de c s'élimine sans calcul (a % de c ne dépasse jamais c).
        const ok = (x) => Number.isFinite(x) && x > 0 && x <= c && Math.abs(x - v) > 1e-9 && Math.abs(x * 10 - Math.round(x * 10)) < 1e-9;
        const cands = [];
        shuffle(pool).forEach((l) => { if (ok(l.v) && !cands.some((m) => Math.abs(m.v - l.v) < 1e-9)) cands.push({ v: Math.round(l.v * 10) / 10, role: l.role }); });
        // Revue 2026-09-08 (A2) : formes homogènes — jamais une seule option à virgule, jamais une seule entière
        // (la bonne, décimale une fois sur deux, était la seule à virgule dans 70 % des tirages).
        const estDec = (x) => !Number.isInteger(x);
        let leurres = null;
        for (let i = 0; i < cands.length && !leurres; i++) for (let j = i + 1; j < cands.length && !leurres; j++) for (let k = j + 1; k < cands.length && !leurres; k++) {
            const nbDec = [v, cands[i].v, cands[j].v, cands[k].v].filter(estDec).length;
            if (nbDec !== 1 && nbDec !== 3) leurres = [cands[i], cands[j], cands[k]];
        }
        if (!leurres) continue;
        const options = shuffle([v, ...leurres.map((l) => l.v)].map(fr));
        const payload = makeQcm(options, fr(v), `<p>Calcule de tête : ${a} % de ${c}.</p>`, opts(L_CALCUL));
        payload.contenu = { ...payload.contenu, sousType: `a${a}` };
        payload._meta.params = { a, c, roles: leurres.map((l) => l.role), signature: `${a}-${c}` };
        const erreurs = erreursSures(erreursCalcul(a, c), v).filter((e) => auDixiemePres(e.v)).map((e) => ({ code: e.code, valeur: fr(e.v) })).filter((e) => options.includes(e.valeur));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1128 : QCM sans leurres');
};
const genCalculQcm = () => antiRepeat(construireCalculQcm, refQ);

// ─── Carte 2 : le bon geste (relier, pur rappel) ───────────────────────────

const GESTES = [
    { a: 100, target: 'prendre le nombre entier' },
    { a: 50, target: 'prendre la moitié' },
    { a: 25, target: 'prendre le quart' },
    { a: 10, target: 'diviser par 10' },
    { a: 1, target: 'diviser par 100' },
];
const GESTES_LEURRES = [
    { target: 'diviser par 25', code: 'divise_par_a' },
    { target: 'enlever 10', code: 'moins_a' },
    { target: 'diviser par 50', code: 'divise_par_a' },
    { target: 'multiplier par 10', code: 'multiplie' },
    { target: 'enlever 1', code: 'moins_a' },
];

const construireGeste = () => {
    const choisis = shuffle(GESTES).slice(0, 4);
    const pairs = choisis.map((g) => ({ source: `${g.a} % d'un nombre`, target: g.target }));
    const leurres = shuffle(GESTES_LEURRES).slice(0, 2);
    const payload = makeRelier(pairs, '<p>Relie chaque pourcentage au geste qui permet de le calculer.</p>', { ...opts(L_GESTE), cibles_leurres: leurres });
    payload._meta.params = { as: choisis.map((g) => g.a).sort((x, y) => y - x), signature: choisis.map((g) => g.a).sort().join('-') + '|' + leurres.map((l) => l.target).sort().join('|') };
    payload.contenu = { ...payload.contenu, cibles_leurres: payload.contenu.cibles_leurres.map((l) => ({ ...l, code: CODE_LEURRE[l.code] })) };
    return payload;
};
const genGeste = () => antiRepeat(construireGeste, refG);

// ─── Carte 3 : quel pourcentage ? (QCM à l'envers) ─────────────────────────

const construireInverse = () => {
    // Revue 2026-09-08 (A8) : a est tiré une fois, puis c est cherché pour ce a (résultat entier) — sinon le
    // cas 100 %, toujours entier, remontait à 18 % au lieu de 10 %.
    const a = tirerA();
    for (let essai = 0; essai < 60; essai++) {
        const c = tirerC(a); const v = valeur(a, c);
        if (!Number.isInteger(v)) continue;                                           // à l'envers, tout en entiers
        const options = shuffle(A.map((x) => `${x} %`));
        const payload = makeQcm(options, `${a} %`, `<p>${v}, c'est quel pourcentage de ${c} ?</p>`, opts(L_GESTE));
        payload.contenu = { ...payload.contenu, sousType: `a${a}` };
        payload._meta.params = { a, c, v, signature: `${a}-${c}` };
        // À l'envers : le repère voisin, « a ÷ 10 », la moitié prise pour 10 %. La réponse c ÷ v (= 100 ÷ a)
        // a deux histoires, « ÷ a » lu à l'envers ou le rapport inversé (500 ÷ 5 = 100, lecture du prof,
        // 03/10/2026) : portée par deux codes, erreursSures ne la nomme jamais.
        const paires = [['divise_par_a', 100 / a], ['inversion', 100 / a], ...VOISINS[a].map((b) => ['pourcentage_voisin', b]), ['pourcent_lu_dixiemes', a / 10], ...(a === 50 ? [['pourcentage_voisin', 10]] : [])];
        const erreurs = erreursSures(paires, a).map((e) => ({ code: e.code, valeur: `${e.v} %` })).filter((e) => options.includes(e.valeur));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1128 : inverse introuvable');
};
const genInverse = () => antiRepeat(construireInverse, refI);

// (La carte « En situation » a été retirée à la demande du prof, 2026-09-08.)

// ─── Carte 5 : vrai ou faux ? (calculs) ────────────────────────────────────

let dernieresLignesVf1128 = [];

const genVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        // Revue 2026-09-08 (A8) : les quatre gestes, et 100 % à la place de l'un d'eux dans 10 % des tirages.
        const as = shuffle([50, 25, 10, 1]); if (Math.random() < 0.1) as[0] = 100;
        const candidates = as.map((a) => {
            const c = tirerC(a); const v = valeur(a, c);
            // Revue 2026-09-08 (A3) : les valeurs fausses restent ≤ c (a % de c ne dépasse jamais c).
            const faux = shuffle([c / a, c - a, v * 10, v / 10, valeur(a === 100 ? 50 : a === 1 ? 10 : a * 2, c)])
                .find((x) => Number.isFinite(x) && x > 0 && x <= c && Math.abs(x - v) > 1e-9 && Math.abs(x * 10 - Math.round(x * 10)) < 1e-9);
            const vrai = Math.random() < 0.5 || faux === undefined;
            // Code de la valeur fausse, si elle est un geste faux nommé sans ambiguïté.
            const code = vrai ? null : (erreursSures(erreursCalcul(a, c, [...VOISINS[a], voisinVf(a)]), v).find((e) => Math.abs(e.v - faux) < 1e-9) || {}).code;
            return { l: `${a} % de ${c}, c'est ${fr(vrai ? v : faux)}.`, v: vrai, f: `a${a}`, code };
        });
        const nbFaux = candidates.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        const lignes = shuffle(candidates);
        if (essai > 120) dernieresLignesVf1128 = [];
        if (lignes.some((it) => dernieresLignesVf1128.includes(it.l))) continue;
        dernieresLignesVf1128 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque calcul, dis s\'il est vrai ou faux.</p>',
            opts(L_CALCUL),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { as };
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1128 : V/F sans tirage après 160 essais');
};

// ─── Carte 6 : les gestes, vrai ou faux ? (pur rappel) ─────────────────────

const POOL_RAPPEL = [
    { l: '50 % d\'un nombre, c\'est sa moitié.', v: true, f: '50' },
    { l: '50 % d\'un nombre, c\'est ce nombre divisé par 50.', v: false, f: '50', code: 'divise_par_a' },
    { l: '25 % d\'un nombre, c\'est son quart.', v: true, f: '25' },
    { l: '25 % d\'un nombre, c\'est ce nombre divisé par 25.', v: false, f: '25', code: 'divise_par_a' },
    { l: '25 % d\'un nombre, c\'est la moitié de sa moitié.', v: true, f: '25' },
    { l: '10 % d\'un nombre, c\'est ce nombre divisé par 10.', v: true, f: '10' },
    { l: '10 % d\'un nombre, c\'est ce nombre moins 10.', v: false, f: '10', code: 'moins_a' },
    { l: '1 % d\'un nombre, c\'est ce nombre divisé par 100.', v: true, f: '1' },
    // ÷ 10 pour 1 % : repère voisin ou % lu en dixièmes, deux codes possibles, aucun n'est rangé.
    { l: '1 % d\'un nombre, c\'est ce nombre divisé par 10.', v: false, f: '1' },
    { l: '100 % d\'un nombre, c\'est le nombre lui-même.', v: true, f: '100' },
    { l: '100 % d\'un nombre, c\'est ce nombre multiplié par 100.', v: false, f: '100', code: 'multiplie_par_a' },
    // Revue 2026-09-08 (prof) : pas de lettres dans les affirmations de rappel.
    { l: '37 % d\'un nombre, c\'est 37 centièmes de ce nombre.', v: true, f: 'sens' },
    { l: 'Un pourcentage d\'un nombre, c\'est toujours plus grand que ce nombre.', v: false, f: 'sens' },
    { l: '10 % d\'un nombre, c\'est 10 fois 1 % de ce nombre.', v: true, f: 'lien' },
    { l: '50 % d\'un nombre, c\'est 2 fois 25 % de ce nombre.', v: true, f: 'lien' },
    { l: '25 % d\'un nombre, c\'est 2 fois 50 % de ce nombre.', v: false, f: 'lien' },
];

let dernieresLignesRappel1128 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1128 = [];
        if (lignes.some((it) => dernieresLignesRappel1128.includes(it.l))) continue;
        dernieresLignesRappel1128 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur les pourcentages, dis si elle est vraie ou fausse.</p>',
            opts(L_RAPPEL),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1128 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    calcul_saisie: genCalculSaisie,
    calcul_qcm_unique: genCalculQcm,
    geste_relier: genGeste,
    geste_qcm_unique: genInverse,
    calcul_tableau_vf: genVf,
    rappel_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', qcm_unique: 'QCM', relier: 'Relier', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'calcul', label: L_CALCUL, formatsList: buildFormats('calcul') },
    { id: 'geste', label: L_GESTE, formatsList: buildFormats('geste') },
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre. Le choix reste proposé,
// il part sans nom.
const CODES_RETIRES = {
    // relecture du 04/10/2026 (E37) : « × 10 » ne survit jamais seul ici (100 % de c pour a = 10, 10 % de c pour
    // a = 1, attribué au hasard ligne 140) ; pourcent_lu_dixiemes ne vit plus qu'en 1127 et 1129
    calcul_qcm_unique: ['pourcent_lu_dixiemes'],
    calcul_tableau_vf: ['pourcent_lu_dixiemes'],
    geste_qcm_unique: ['pourcentage_voisin', 'pourcent_lu_dixiemes'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};
